import React from 'react'
import { FiBarChart, FiMousePointer } from 'react-icons/fi'
import { IoMdStopwatch } from 'react-icons/io'
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import Card from '../../../../../../components/ui/Card'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectMenuItemLoadingAnalytics } from '../../../../../../store/selectors'
import Skeleton from '../../../../../../components/ui/Skeleton'

interface MobileAnalyticsTabProps {
	cardVariants: {
		hidden: {
			opacity: number;
			y: number;
		};
		visible: {
			opacity: number;
			y: number;
		};
	},
	itemAnalytics: {
		id: string;
		year: number;
		month: number;
		click_count: number;
		time_spent: number;
	}[],
	analyticsAverages: {
		avgClicks: number;
		avgTimeSpent: number;
	} | {
		avgClicks: number;
		avgTimeSpent: string;
	}
}

const MobileAnalyticsTab: React.FC<MobileAnalyticsTabProps> = ({ cardVariants, itemAnalytics, analyticsAverages }) => {
	const { t, i18n } = useTranslation()

	const isLoadingAnalytics = useAppSelector(selectMenuItemLoadingAnalytics)

	// Prepare analytics data for charts
	const prepareAnalyticsData = () => {
		if (!itemAnalytics.length) return [];

		return itemAnalytics.map(item => ({
			period: `${item.year}-${String(item.month).padStart(2, '0')}`,
			clicks: item.click_count,
			timeSpent: parseFloat((item.time_spent / 60).toFixed(1)), // Convert to minutes
			...item
		})).sort((a, b) => {
			if (a.year !== b.year) return a.year - b.year;
			return a.month - b.month;
		});
	};

	const chartData = prepareAnalyticsData();

	return (
		<motion.div variants={cardVariants}>
			<Card className="p-4 sm:p-6 border-none shadow-md">
				<div className="flex items-center gap-3 mb-4 sm:mb-6">
					<div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
						<FiBarChart className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
					</div>
					<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
						{t('menuItems.itemAnalytics')}
					</h3>
				</div>

				{isLoadingAnalytics ? (
					<div className="space-y-4">
						<Skeleton width="w-full" height="h-48" />
						<div className="grid grid-cols-2 gap-4">
							<Skeleton width="w-full" height="h-20" />
							<Skeleton width="w-full" height="h-20" />
						</div>
					</div>
				) : itemAnalytics.length > 0 ? (
					<div className="space-y-6">
						{/* Analytics Summary Cards */}
						<div className="grid grid-cols-2 gap-4">
							<div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
								<div className="flex items-center gap-2 mb-2">
									<FiMousePointer className="w-4 h-4 text-blue-600 dark:text-blue-400" />
									<span className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400">
										{t('menuItems.avgClicks')}
									</span>
								</div>
								<div className="text-xl sm:text-2xl font-bold text-blue-700 dark:text-blue-300">
									{analyticsAverages.avgClicks}
								</div>
								<div className="text-xs text-blue-600 dark:text-blue-400">
									{t('menuItems.clicks')} {t('menuItems.perMonth')}
								</div>
							</div>

							<div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
								<div className="flex items-center gap-2 mb-2">
									<IoMdStopwatch className="w-4 h-4 text-green-600 dark:text-green-400" />
									<span className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400">
										{t('menuItems.avgTimeSpent')}
									</span>
								</div>
								<div className="text-xl sm:text-2xl font-bold text-green-700 dark:text-green-300">
									{analyticsAverages.avgTimeSpent}
								</div>
								<div className="text-xs text-green-600 dark:text-green-400">
									{t('menuItems.minutes')} {t('menuItems.perMonth')}
								</div>
							</div>
						</div>

						{/* Charts */}
						<div className="space-y-6">
							{/* Clicks Chart */}
							<div>
								<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
									{t('menuItems.clicksOverTime')}
								</h4>
								<div className="h-48 sm:h-64">
									<ResponsiveContainer width="100%" height="100%">
										<LineChart data={chartData}>
											<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
											<XAxis
												dataKey="period"
												tick={{ fontSize: 12 }}
												stroke="#6b7280"
											/>
											<YAxis
												tick={{ fontSize: 12 }}
												stroke="#6b7280"
											/>
											<Tooltip
												labelStyle={{ color: '#374151' }}
												contentStyle={{
													backgroundColor: '#f9fafb',
													border: '1px solid #e5e7eb',
													borderRadius: '8px'
												}}
											/>
											<Line
												type="monotone"
												dataKey="clicks"
												stroke="#3b82f6"
												strokeWidth={2}
												dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
												activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
											/>
										</LineChart>
									</ResponsiveContainer>
								</div>
							</div>

							{/* Time Spent Chart */}
							<div>
								<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
									{t('menuItems.timeSpentOverTime')}
								</h4>
								<div className="h-48 sm:h-64">
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={chartData}>
											<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
											<XAxis
												dataKey="period"
												tick={{ fontSize: 12 }}
												stroke="#6b7280"
											/>
											<YAxis
												tick={{ fontSize: 12 }}
												stroke="#6b7280"
												label={{
													value: t('menuItems.minutes'),
													angle: -90,
													position: 'insideLeft',
													style: { textAnchor: 'middle' }
												}}
											/>
											<Tooltip
												labelStyle={{ color: '#374151' }}
												contentStyle={{
													backgroundColor: '#f9fafb',
													border: '1px solid #e5e7eb',
													borderRadius: '8px'
												}}
												formatter={(value) => [`${value}m`, t('menuItems.timeSpent')]}
											/>
											<Bar
												dataKey="timeSpent"
												fill="#10b981"
												radius={[4, 4, 0, 0]}
											/>
										</BarChart>
									</ResponsiveContainer>
								</div>
							</div>

							{/* Combined Chart */}
							<div>
								<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
									{t('menuItems.combinedAnalytics')}
								</h4>
								<div className="h-48 sm:h-64">
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={chartData}>
											<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
											<XAxis
												dataKey="period"
												tick={{ fontSize: 12 }}
												stroke="#6b7280"
											/>
											<YAxis
												tick={{ fontSize: 12 }}
												stroke="#6b7280"
											/>
											<Tooltip
												labelStyle={{ color: '#374151' }}
												contentStyle={{
													backgroundColor: '#f9fafb',
													border: '1px solid #e5e7eb',
													borderRadius: '8px'
												}}
											/>
											<Legend />
											<Bar
												dataKey="clicks"
												fill="#3b82f6"
												name={t('menuItems.clicks')}
												radius={[2, 2, 0, 0]}
											/>
											<Bar
												dataKey="timeSpent"
												fill="#10b981"
												name={t('menuItems.timeSpentMinutes')}
												radius={[2, 2, 0, 0]}
											/>
										</BarChart>
									</ResponsiveContainer>
								</div>
							</div>
						</div>
					</div>
				) : (
					<div className="text-center py-8 sm:py-12">
						<FiBarChart className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
						<h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
							{t('menuItems.noAnalyticsData')}
						</h4>
						<p className="text-sm text-gray-600 dark:text-gray-400">
							{t('menuItems.noAnalyticsDescription')}
						</p>
					</div>
				)}
			</Card>
		</motion.div>
	)
}

export default MobileAnalyticsTab